/**
 * Pick the board's density from the height it actually has — "fill the screen, no scrolling".
 *
 * **It measures overflow; it does not predict it.** An earlier version scaled a measured row by a
 * table of per-tier ratios to guess what the other tiers would cost, and that guess oscillated: row
 * height here is set by the line code's text, which barely changes between tiers, while the ratios
 * described the track height, which changes a lot. From `sm` it predicted an `md` row of 37px and
 * stepped up; `md` measured 45px and did not fit, so it stepped back down, and the board flickered
 * between the two forever.
 *
 * The rule now is a walk with a memory. Step down while the table overflows, step up only into a
 * tier not already known to overflow at this size. Every decision comes from
 * `scrollHeight > clientHeight` on the real container, so a tier is only ever rejected after being
 * seen to fail — which is what makes the walk terminate.
 *
 * **Not a §4.8 violation:** nothing here writes a font size or any other style. The measurement
 * chooses one of four prebuilt Tailwind class sets and never becomes CSS.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

import type { Tier } from './columns';

/** Largest first — stepping "down" is moving right, and `sm` is the floor. */
const ORDER: readonly Tier[] = ['xl', 'lg', 'md', 'sm'];

/** Slack, so a board that clears by a pixel does not overflow when a figure re-wraps. */
const SAFETY_PX = 8;

export interface FittedTier {
  /** Put this on the element that owns the height available to the table. */
  ref: RefObject<HTMLDivElement | null>;
  tier: Tier;
  /**
   * Rows the screen can hold, once the smallest tier still is not enough. `null` means every row
   * fits and the board shows them all; a number means the caller pages through them.
   */
  pageSize: number | null;
}

export function useFittedTier(kiosk: boolean, rowCount: number): FittedTier {
  const ref = useRef<HTMLDivElement>(null);
  // `md`, not `xl`: the first paint precedes any measurement, so most boards settle by growing once
  // rather than cascading down through three tiers in front of the reader.
  const [tier, setTier] = useState<Tier>('md');

  /**
   * Tiers seen to overflow, keyed by the viewport and the number of rows. Without this the walk has
   * no memory, and "step up because there is room" undoes "step down because it did not fit" on the
   * very next measurement.
   *
   * **The key must not depend on the tier.** Keying it on the measured height — the obvious choice —
   * reintroduces the flicker by a longer route: the headline strip and the legend scale their
   * padding and type with the tier, so every tier change alters the height left for the board,
   * which changed the key, which wiped the record of what had already failed. Tier → chrome height
   * → available height → tier is a loop; the viewport is the one term outside it.
   */
  const rejected = useRef<{ key: string; tiers: Set<Tier> }>({ key: '', tiers: new Set() });

  /**
   * How many rows fit once the board has bottomed out and still overflows.
   *
   * Measured **once** per key and then left alone. Re-deriving it would loop for the same reason
   * the tier walk did: paging makes the rows fit, `fitToScreen` then stretches them to fill the
   * height, the taller rows measure as fewer-per-screen, and the page shrinks on every pass.
   */
  const [pageSize, setPageSize] = useState<number | null>(null);
  const sized = useRef<string>('');

  useEffect(() => {
    const element = ref.current;
    if (!kiosk || element === null || rowCount === 0) {
      return;
    }

    let frame = 0;

    /**
     * Always measure on a later frame, never in the observer callback itself. `observe()` fires
     * synchronously, which for a tier change means measuring the layout the tier just replaced —
     * the board then reads "there is room", stops, and sits one tier too large with the table
     * quietly scrolling. Two frames is what it takes for the new class set to be laid out.
     */
    const measure = () => {
      const container = element.querySelector('[data-slot=table-container]');
      if (container === null) {
        return;
      }
      const available = container.clientHeight;
      if (available <= 0) {
        return;
      }

      const key = `${String(window.innerHeight)}x${String(window.innerWidth)}x${String(rowCount)}`;
      if (rejected.current.key !== key) {
        rejected.current = { key, tiers: new Set() };
      }
      const tiers = rejected.current.tiers;

      const index = ORDER.indexOf(tier);
      if (index === -1) {
        return;
      }

      // Ground truth, not a model: this tier is on screen, and the browser has laid it out.
      if (container.scrollHeight > available + SAFETY_PX) {
        const smaller = ORDER[index + 1];
        if (smaller !== undefined) {
          tiers.add(tier);
          setTier(smaller);
          return;
        }

        // Out of tiers: the screen genuinely cannot hold this floor. Work out how many rows it can
        // hold and hand that back — the caller rotates through the rest.
        if (sized.current !== key) {
          const row = element.querySelector('tbody tr');
          const head = element.querySelector('thead');
          const rowHeight = row?.getBoundingClientRect().height ?? 0;
          if (rowHeight > 0) {
            const body = available - (head?.getBoundingClientRect().height ?? 0) - SAFETY_PX;
            sized.current = key;
            setPageSize(Math.max(1, Math.floor(body / rowHeight)));
          }
        }
        return;
      }

      // Everything on screen fits. Only drop the paging if this is the whole result, not one page
      // of it — otherwise the board would page, fit, un-page, overflow, and page again.
      if (pageSize !== null && sized.current !== key) {
        setPageSize(null);
      }

      // Room to spare. Grow only into a tier this size has not already rejected, or the board
      // steps back into the tier it just failed and starts flickering again.
      const larger = ORDER[index - 1];
      if (larger !== undefined && !tiers.has(larger)) {
        setTier(larger);
      }
    };

    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(measure);
      });
    };

    // Once for this tier, then again whenever the box genuinely resizes.
    schedule();
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [kiosk, rowCount, tier, pageSize]);

  return { ref, tier: kiosk ? tier : 'desk', pageSize: kiosk ? pageSize : null };
}
